/**
 * Locale-agnostic numeric input handler for the column-filter popovers
 * (`FilterNumericRange`). Mirrors the inline `onChange` body in
 * `src/components/inputs/Input.jsx` (around lines 145-196) so the column-
 * filter popovers and the Profiler criteria search form interpret
 * typed/pasted decimals identically: both `8.5` and `8,5` resolve to the
 * same internal value regardless of browser locale, and bad input is
 * silently rejected (the in-flight character simply doesn't appear) rather
 * than surfaced with an inline error.
 *
 * If either parser is ever touched, please keep the other in sync. The
 * shared rules are:
 *   - Single `.replace(',', '.')` (one comma → period, locale-agnostic).
 *   - Regex /^-?\d+\.?(\d{1,2})?$/ for decimals; /^-?\d+$/ for integers.
 *   - When `allowNegative` is false the leading `-` is silently stripped
 *     (so the user can't type `-` at all on Employees, matching the search
 *     form where EMPLOYEES_MIN has `MIN: 0`).
 *
 * Intentional divergence from Input.jsx: the Input.jsx leading-zero-strip
 * loop uses `splice(1, 1)` which removes the digit AFTER the leading zero
 * (so `08` ends up as `0`, dropping the `8`). That's a latent search-form
 * bug we don't want to copy here; we just leave the literal input alone
 * and let the user clean up a stray leading zero themselves. Number()
 * parses `08` as `8` either way, so the committed numeric value is correct.
 */

const DECIMAL_REGEX = /^-?\d+\.?(\d{1,2})?$/;
const INTEGER_REGEX = /^-?\d+$/;

/**
 * Accept-or-reject a keystroke against the column's parsing rules.
 *
 * Direct port of the Input.jsx onChange body. Returns the string that
 * should appear in the input field after this keystroke. On a rejected
 * keystroke that returns `oldValue` — the input visually does not change,
 * exactly as the search-form numeric inputs behave today.
 *
 * @param {string} newValue        — the raw `e.target.value`.
 * @param {string} oldValue        — the previous value to revert to on reject.
 * @param {object} [opts]
 * @param {boolean} [opts.integer=false]
 * @param {boolean} [opts.allowNegative=true]
 * @returns {string} — the string to commit to the input's `value`.
 */
export function processNumericInput(newValue, oldValue, opts = {}) {
  const integer = opts.integer === true;
  const allowNegative = opts.allowNegative !== false;
  const safe = newValue == null ? '' : String(newValue);

  // Empty + transient lone `-` (when negative is allowed) pass through
  // unchanged so the user can type `-5` one keystroke at a time.
  if (safe === '') return '';
  if (safe === '-' && allowNegative) return '-';

  // Locale-agnostic comma → period (single replacement, same as Input.jsx).
  // The regex below rejects `1,234.5` style thousands separators because the
  // first comma becomes a dot, leaving `1.234.5` which fails the multi-dot
  // check.
  let normalized = safe.replace(',', '.');

  // Strip any `-` when negative is not allowed (mirrors Input.jsx). The
  // search form uses this to make Employees non-negative without showing
  // an error — the `-` just never appears in the input.
  if (safe.startsWith('-') && !allowNegative) {
    normalized = normalized.replaceAll('-', '');
  }

  const pattern = integer ? INTEGER_REGEX : DECIMAL_REGEX;
  if (!pattern.test(normalized)) {
    return oldValue;
  }
  return normalized;
}

/**
 * Parse an already-accepted display string into a number suitable for
 * storage in `range.min` / `range.max`. Returns `null` for empty input or
 * the transient `-` typing state — both mean "don't commit a numeric
 * filter on this side yet".
 *
 * Display-scale multiplication is the caller's responsibility (the helper
 * is unit-agnostic).
 *
 * @param {string} acceptedStr — the string `processNumericInput` returned.
 * @returns {number | null}
 */
export function parseAcceptedNumber(acceptedStr) {
  if (acceptedStr === '' || acceptedStr === '-' || acceptedStr == null) {
    return null;
  }
  const n = Number(acceptedStr);
  return Number.isFinite(n) ? n : null;
}

/**
 * Per-column parsing options. Mirrors the field-definition flags on
 * REVENUE_MIN / EMPLOYEES_MIN / EBIT_MARGIN_MIN / CAGR_MIN in
 * `src/utils/constants/fields.js`:
 *   - Employees is integer-only and non-negative (`INTEGER: true, MIN: 0`).
 *   - Sales, EBIT Margin and CAGR are decimal and allow negatives (the
 *     search form has no `MIN` on those fields).
 */
export function parserOptsForColumn(columnKey) {
  return {
    integer: columnKey === 'employees',
    allowNegative: columnKey !== 'employees'
  };
}
